import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/Brand";
import { ClipboardList, ArrowRight, Trash2 } from "lucide-react";

export default function Inspections() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const nav = useNavigate();
  const canEdit = user?.role !== "viewer";

  const load = useCallback(() => {
    api.get("/inspections").then((r) => setItems(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this inspection and all its findings? This cannot be undone.")) return;
    try {
      await api.delete(`/inspections/${id}`);
      toast.success("Inspection deleted");
      load();
    } catch (err) { toast.error(apiErr(err)); }
  };

  return (
    <div className="space-y-6" data-testid="inspections-page">
      <div>
        <h1 className="font-display text-3xl font-bold uppercase tracking-tight text-slate-900">Inspections</h1>
        <p className="text-sm text-slate-500">All unit walkthroughs across your properties</p>
      </div>

      {items.length === 0 && (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-16 text-center">
          <ClipboardList className="mx-auto h-10 w-10 text-slate-300" />
          <p className="mt-3 text-sm text-slate-500">No inspections yet. Start one from a unit.</p>
        </div>
      )}

      <div className="space-y-3">
        {items.map((i) => (
          <div key={i.id} data-testid={`inspection-row-${i.id}`} onClick={() => nav(`/app/inspections/${i.id}`)}
            className="flex cursor-pointer items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-5 text-left shadow-sm transition-all hover:border-blue-300 hover:shadow-md">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-display text-lg font-bold text-slate-900">{i.property_name} · Unit {i.unit_number}</span>
                <StatusBadge status={i.status} />
              </div>
              <div className="mt-1 text-xs text-slate-500">{i.inspection_date} · {i.inspector_name} · {i.finding_count} findings</div>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              {canEdit && (
                <button data-testid={`delete-inspection-${i.id}`} onClick={(e) => remove(i.id, e)}
                  title="Delete inspection"
                  className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-600">
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
              <ArrowRight className="h-4 w-4 text-slate-400" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
